/**
 * Owner: Masa Bayu
 * File: reward-withdrawal.ts
 * Date:Time: 2026-09-15 19:45
 * Description: T0 Affiliate/Referral reward balance + cash withdrawal contracts (≠ T3).
 */
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { assertNoForbiddenFoundationKey } from './authority';
/**
 * LOCKED: Earn automatically → Balance confirmed → Cash withdrawal.
 * Affiliate & Referral rewards are real withdrawable earnings (not points).
 */
export const QXREAD_REWARD_WITHDRAWAL_INVARIANTS = Object.freeze({
    affiliateRewardWithdrawable: true,
    referralRewardWithdrawable: true,
    rewardsAreBuyerFunded: true,
    sellerPriceDoesNotFundRewards: true,
    rewardNotImmediatelyAvailableAtCheckout: true,
    rewardLedgerNotRbs: true,
    rewardLedgerNotServiceFee: true,
    uiCannotMintOrMutateRewardBalance: true,
    withdrawalRequiresAuthoritativeLedger: true,
    affiliateAndReferralBalancesRemainSeparate: true,
});
/** Balance lifecycle — Available only after T3 settlement conditions. */
export const QXREAD_REWARD_BALANCE_STATES = [
    'pending',
    'confirmed',
    'available',
    'withdrawal_requested',
    'paid',
];
/** Source ledgers for rewards — distinct for audit. */
export const QXREAD_REWARD_LEDGER_SOURCES = ['affiliate', 'referral'];
function assertRewardState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_REWARD_BALANCE_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_REWARD_BALANCE_STATES.join(', ')}`);
    }
    return value;
}
function assertRewardSource(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_REWARD_LEDGER_SOURCES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_REWARD_LEDGER_SOURCES.join(', ')}`);
    }
    return value;
}
function assertMinorUnits(value, label) {
    if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
        throw new Error(`${label} must be a finite integer`);
    }
    if (value < 0) {
        throw new Error(`${label} must not be negative`);
    }
    return value;
}
export function isWithdrawableRewardLedgerConcept(concept) {
    return concept === 'affiliate' || concept === 'referral';
}
/** Checkout posts Pending only — never Available until T3 settlement confirms. */
export function initialRewardBalanceStateAfterSale() {
    return 'pending';
}
export function createQxreadRewardBalanceContract(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead reward balance');
    assertValidId(options.accountId, 'QXRead reward accountId');
    const balanceId = options.balanceId !== undefined ? options.balanceId : createQxreadId('qxrdbal');
    assertValidId(balanceId, 'QXRead reward balanceId');
    const updatedAt = options.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead reward updatedAt');
    const currency = (options.currency ?? 'USD').trim().toUpperCase();
    if (currency.length === 0) {
        throw new Error('QXRead reward currency is required');
    }
    return Object.freeze({
        balanceId,
        accountId: options.accountId,
        source: assertRewardSource(options.source, 'QXRead reward source'),
        state: assertRewardState(options.state ?? initialRewardBalanceStateAfterSale(), 'QXRead reward state'),
        amountMinorUnits: assertMinorUnits(options.amountMinorUnits, 'QXRead reward amount'),
        currency,
        updatedAt,
    });
}
/**
 * Withdrawal request contract — authoritative ledger only (T3).
 * Requires Available balance; T0 does not execute payout.
 */
export function createQxreadWithdrawalRequestContract(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead withdrawal request');
    if (options.balanceState !== 'available') {
        throw new Error('QXRead withdrawal requires Available balance (not Pending/Confirmed at checkout)');
    }
    assertValidId(options.accountId, 'QXRead withdrawal accountId');
    const amountMinorUnits = assertMinorUnits(options.amountMinorUnits, 'QXRead withdrawal amount');
    if (amountMinorUnits === 0) {
        throw new Error('QXRead withdrawal amount must be greater than zero');
    }
    const withdrawalId = options.withdrawalId !== undefined ? options.withdrawalId : createQxreadId('qxrdwd');
    assertValidId(withdrawalId, 'QXRead withdrawalId');
    const requestedAt = options.requestedAt ?? new Date().toISOString();
    assertTimestamp(requestedAt, 'QXRead withdrawal requestedAt');
    const currency = (options.currency ?? 'USD').trim().toUpperCase();
    if (currency.length === 0) {
        throw new Error('QXRead withdrawal currency is required');
    }
    return Object.freeze({
        withdrawalId,
        accountId: options.accountId,
        source: assertRewardSource(options.source, 'QXRead withdrawal source'),
        amountMinorUnits,
        currency,
        requestedAt,
    });
}
/** Display sum only — Affiliate and Referral source ledgers stay separate for audit. */
export function projectTotalAvailableRewards(input) {
    const affiliateAvailableMinorUnits = assertMinorUnits(input.affiliateAvailableMinorUnits, 'QXRead affiliateAvailableMinorUnits');
    const referralAvailableMinorUnits = assertMinorUnits(input.referralAvailableMinorUnits, 'QXRead referralAvailableMinorUnits');
    const currency = (input.currency ?? 'USD').trim().toUpperCase();
    return Object.freeze({
        affiliateAvailableMinorUnits,
        referralAvailableMinorUnits,
        totalAvailableMinorUnits: affiliateAvailableMinorUnits + referralAvailableMinorUnits,
        currency,
        note: 'display_sum_only_sources_remain_separate',
    });
}
export function assertRewardWithdrawalInvariantsIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_REWARD_WITHDRAWAL_INVARIANTS)) {
        const expected = QXREAD_REWARD_WITHDRAWAL_INVARIANTS[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead reward withdrawal invariant violated: ${key}`);
        }
    }
    return QXREAD_REWARD_WITHDRAWAL_INVARIANTS;
}
//# sourceMappingURL=reward-withdrawal.js.map